import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Save } from 'lucide-react';
import { updateHoldingFiscalProfile } from '../../../../../api/operatorHoldings';
import { getCommunes, getRegions } from '../../../../../api/contracts';
import { useCountry } from '../../../../../context/CountryContext';
import { showToast } from '../../../../../utils/showToast';
import { normalizeSearchText } from '../../../../../utils/commerceSearch';
import { Button } from '../../../../../components/ui/Button';
import { Input } from '../../../../../components/ui/Input';
import { FieldLabel } from '../../../../../components/ui/FieldLabel';
import { cn } from '../../../../../utils/cn';
import {
  buildFiscalPayload,
  fiscalFormDirty,
  fiscalToForm,
  validateFiscalForm,
  MAX_ACTIVITY_LENGTH,
  MAX_DIVISION_LENGTH,
  MAX_STREET_LENGTH,
} from '../../utils/holdingForms';
import { holdingErrorText } from '../../utils/holdings';

const selectCls =
  'h-10 w-full rounded-lg border bg-bg px-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50';

const sameText = (a, b) => normalizeSearchText(a) === normalizeSearchText(b);

/**
 * Catálogo de regiones del país del operador y comunas de la región elegida. Una región puede
 * venir con sus comunas anidadas; si no, se piden aparte. Si el catálogo no carga, la vista
 * cae a texto libre en vez de bloquear la carga de los datos.
 */
function useDivisionCatalog(stateValue) {
  const { countryId, loading: countryLoading } = useCountry();
  const [regions, setRegions] = useState(null);
  const [communesByRegion, setCommunesByRegion] = useState({});

  useEffect(() => {
    if (countryLoading || !countryId) return undefined;
    let alive = true;
    Promise.resolve(getRegions({ countryId }))
      .then((data) => {
        if (alive) setRegions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (alive) setRegions([]);
      });
    return () => {
      alive = false;
    };
  }, [countryId, countryLoading]);

  // Región del catálogo que corresponde al valor guardado: por nombre (con o sin tildes) o por
  // código, que es lo que guarda el alta de empresas en su dirección fiscal.
  const region = useMemo(
    () =>
      (regions ?? []).find(
        (r) => sameText(r?.name, stateValue) || (r?.code != null && String(r.code) === String(stateValue)),
      ) ?? null,
    [regions, stateValue],
  );
  const regionKey = region ? String(region.code ?? region.name) : '';

  useEffect(() => {
    if (!region || communesByRegion[regionKey]) return undefined;
    if (Array.isArray(region.communes) && region.communes.length > 0) {
      setCommunesByRegion((m) => ({ ...m, [regionKey]: region.communes }));
      return undefined;
    }
    let alive = true;
    Promise.resolve(getCommunes({ countryId, regionId: region.code }))
      .then((data) => {
        if (alive) setCommunesByRegion((m) => ({ ...m, [regionKey]: Array.isArray(data) ? data : [] }));
      })
      .catch(() => {
        if (alive) setCommunesByRegion((m) => ({ ...m, [regionKey]: [] }));
      });
    return () => {
      alive = false;
    };
  }, [region, regionKey, countryId, communesByRegion]);

  return {
    regions,
    region,
    communes: region ? communesByRegion[regionKey] ?? null : null,
  };
}

/**
 * Datos fiscales del holding (microservice-contracts): giro y dirección fiscal con comuna y
 * región del catálogo del país. La razón social y el RUT se muestran pero NO se editan: contracts
 * no los acepta por API (el RUT de las empresas hijas se deriva del del holding).
 *
 * Se usa en la ficha del holding y en el paso "Datos del holding" del asistente de cobro.
 * El formulario se siembra al montar: para OTRO holding, quien lo usa lo remonta con `key`.
 *
 * @param {{ holding: object, onSaved?: (view: object) => void }} props
 *   `holding` es un elemento de GET contracts/holdings; `onSaved` recibe la vista del PATCH
 *   ({ id, name, legalName, businessIdentifier, activity, fiscalAddress, missingFiscalFields }).
 */
export default function HoldingFiscalForm({ holding, onSaved }) {
  const { t } = useTranslation();
  const baseId = useId();
  const { labels } = useCountry();
  const regionLabel = labels?.region ?? 'Región';
  const communeLabel = labels?.commune ?? 'Comuna';

  const [initial, setInitial] = useState(() => fiscalToForm(holding));
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const { regions, region, communes } = useDivisionCatalog(form.state);
  const commune = useMemo(
    () => (communes ?? []).find((c) => sameText(c?.name, form.municipality)) ?? null,
    [communes, form.municipality],
  );
  // Sin catálogo (no cargó, o el país no tiene regiones) se escribe a mano.
  const freeText = Array.isArray(regions) && regions.length === 0;

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaveError(null);
  };

  const dirty = fiscalFormDirty(form, initial);
  const missingIdentity = !holding?.legalName || !holding?.businessIdentifier;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateFiscalForm(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = buildFiscalPayload(form, {
        state: region?.name,
        municipality: commune?.name,
      });
      const { data } = await updateHoldingFiscalProfile(holding.id, body);
      // Lo guardado pasa a ser el punto de partida (con los nombres del catálogo que se
      // mandaron). Se siembra desde la respuesta y no desde la prop: resembrar al cambiar la
      // prop pisaría lo que se esté tecleando si el padre re-renderiza.
      const saved = fiscalToForm(data);
      setInitial(saved);
      setForm(saved);
      showToast(t('Datos fiscales del holding guardados.'), 'success');
      onSaved?.(data);
    } catch (err) {
      const text = holdingErrorText(err, t, 'No se pudieron guardar los datos fiscales del holding.');
      setSaveError(text);
      showToast(text, 'error');
    } finally {
      setSaving(false);
    }
  };

  const ids = {
    activity: `${baseId}-activity`,
    street: `${baseId}-street`,
    state: `${baseId}-state`,
    municipality: `${baseId}-municipality`,
    city: `${baseId}-city`,
  };
  const fieldError = (field) =>
    errors[field] ? (
      <p id={`${ids[field]}-error`} className="text-xs text-status-error">
        {t(errors[field])}
      </p>
    ) : null;
  const describedBy = (field) => (errors[field] ? `${ids[field]}-error` : undefined);

  // Opción del valor guardado cuando no está en el catálogo: sin ella el select lo mostraría
  // vacío y guardar lo borraría sin que el operador lo note.
  const regionValue = region?.name ?? form.state;
  const communeValue = commune?.name ?? form.municipality;
  const regionOptions = (regions ?? []).map((r) => r.name).filter(Boolean);
  const communeOptions = (communes ?? []).map((c) => c.name).filter(Boolean);

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
            {t('Razón social')}
          </dt>
          <dd className="text-sm text-fg">{holding?.legalName || '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
            {t('RUT')}
          </dt>
          <dd className="text-sm text-fg">{holding?.businessIdentifier || '—'}</dd>
        </div>
      </dl>
      {missingIdentity && (
        <p className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {t('Falta la razón social o el RUT del holding. No se editan desde acá: pedile a soporte de Beartrack que los cargue. Sin ellos la nota del holding no se puede emitir.')}
          </span>
        </p>
      )}

      <div className="space-y-1">
        <FieldLabel htmlFor={ids.activity}>{t('Giro')}</FieldLabel>
        <Input
          id={ids.activity}
          value={form.activity}
          maxLength={MAX_ACTIVITY_LENGTH}
          invalid={!!errors.activity}
          aria-describedby={describedBy('activity')}
          placeholder={t('Ej: Venta al por menor por internet')}
          onChange={(e) => update({ activity: e.target.value })}
        />
        {fieldError('activity')}
      </div>

      <div className="space-y-1">
        <FieldLabel htmlFor={ids.street}>{t('Calle y número')}</FieldLabel>
        <Input
          id={ids.street}
          value={form.street}
          maxLength={MAX_STREET_LENGTH}
          invalid={!!errors.street}
          aria-describedby={describedBy('street')}
          onChange={(e) => update({ street: e.target.value })}
        />
        {fieldError('street')}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <FieldLabel htmlFor={ids.state}>{t(regionLabel)}</FieldLabel>
          {freeText ? (
            <Input
              id={ids.state}
              value={form.state}
              maxLength={MAX_DIVISION_LENGTH}
              invalid={!!errors.state}
              aria-describedby={describedBy('state')}
              onChange={(e) => update({ state: e.target.value })}
            />
          ) : (
            <select
              id={ids.state}
              className={cn(selectCls, errors.state ? 'border-status-error' : 'border-border')}
              value={regionValue}
              disabled={regions === null}
              aria-describedby={describedBy('state')}
              onChange={(e) => update({ state: e.target.value, municipality: '' })}
            >
              <option value="">{regions === null ? t('Cargando…') : t('Selecciona…')}</option>
              {regionValue && !region && <option value={regionValue}>{regionValue}</option>}
              {regionOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {fieldError('state')}
        </div>

        <div className="space-y-1">
          <FieldLabel htmlFor={ids.municipality}>{t(communeLabel)}</FieldLabel>
          {freeText || (!region && form.state && regions !== null) ? (
            <Input
              id={ids.municipality}
              value={form.municipality}
              maxLength={MAX_DIVISION_LENGTH}
              invalid={!!errors.municipality}
              aria-describedby={describedBy('municipality')}
              onChange={(e) => update({ municipality: e.target.value })}
            />
          ) : (
            <select
              id={ids.municipality}
              className={cn(selectCls, errors.municipality ? 'border-status-error' : 'border-border')}
              value={communeValue}
              disabled={!region || communes === null}
              aria-describedby={describedBy('municipality')}
              onChange={(e) => update({ municipality: e.target.value })}
            >
              <option value="">
                {region && communes === null ? t('Cargando…') : t('Selecciona…')}
              </option>
              {communeValue && !commune && <option value={communeValue}>{communeValue}</option>}
              {communeOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {fieldError('municipality')}
        </div>
      </div>

      <div className="space-y-1">
        <FieldLabel htmlFor={ids.city}>{t('Ciudad (opcional)')}</FieldLabel>
        <Input
          id={ids.city}
          value={form.city}
          maxLength={MAX_DIVISION_LENGTH}
          invalid={!!errors.city}
          aria-describedby={describedBy('city')}
          onChange={(e) => update({ city: e.target.value })}
        />
        {fieldError('city')}
      </div>

      {saveError && (
        <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setForm(initial);
            setErrors({});
            setSaveError(null);
          }}
          disabled={!dirty || saving}
        >
          {t('Descartar cambios')}
        </Button>
        <Button type="submit" loading={saving} disabled={!dirty} iconLeft={<Save className="h-4 w-4" />}>
          {t('Guardar datos fiscales')}
        </Button>
      </div>
    </form>
  );
}
